import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Divider,
  useTheme,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  LockOutlined as LockOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Save as SaveIcon,
  Preview as PreviewIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
  Crop as CropIcon,
  Close as CloseIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LivePreviewCard from '../components/LivePreviewCard';
import { getConfig } from '../components/CardRenderer';
import { sortCardSections } from '../utils/sortCardSections';
import {
  parseCardFromApi,
  serializeStructuredCard,
  DEFAULT_CARD_SECTIONS,
  LOCKED_SECTION_IDS,
} from '../utils/cardDataStructure';
import BannerCropDialog from '../components/BannerCropDialog';
import BannerStrip from '../components/BannerStrip';
import {
  BANNER_EXPORT_WIDTH,
  BANNER_EXPORT_HEIGHT,
  PROFILE_EXPORT_WIDTH,
  PROFILE_EXPORT_HEIGHT,
} from '../utils/bannerStripStyles';
import { GALLERY_VIEW_TYPES, GALLERY_LAYOUT_LABELS } from '../utils/galleryLayout';
import { GalleryLayoutIcon } from '../components/GalleryViewTypeIcons';
import AboutMuiTiptapField from '../components/AboutMuiTiptapField';

const MAX_GALLERY_IMAGES = (() => {
  const n = parseInt(process.env.REACT_APP_MAX_GALLERY_IMAGES || '6', 10);
  return Number.isFinite(n) && n >= 1 ? n : 6;
})();

function galleryFilenameFromUrl(url) {
  if (!url || typeof url !== 'string') return 'Image';
  try {
    const path = url.split('?')[0];
    const seg = path.split('/').pop() || 'Image';
    return decodeURIComponent(seg);
  } catch {
    return 'Image';
  }
}

// Focus-preserving Text Field Component
const FocusTextField = React.memo(({ label, value, onChange, placeholder, multiline, rows, ...props }) => {
  const [localValue, setLocalValue] = useState(value || '');
  const debouncedUpdate = useRef(null);
  const inputRef = useRef(null);
  const isFocused = useRef(false);

  // Only update local value if we're not focused
  useEffect(() => {
    if (!isFocused.current && value !== localValue) {
      setLocalValue(value || '');
    }
  }, [value, localValue]);

  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    
    if (debouncedUpdate.current) {
      clearTimeout(debouncedUpdate.current);
    }
    debouncedUpdate.current = setTimeout(() => {
      if (onChange) {
        onChange(newValue);
      }
    }, 300);
  }, [onChange]);

  const handleFocus = useCallback(() => {
    isFocused.current = true;
  }, []);

  const handleBlur = useCallback(() => {
    isFocused.current = false;
  }, []);

  useEffect(() => {
    return () => {
      if (debouncedUpdate.current) {
        clearTimeout(debouncedUpdate.current);
      }
    };
  }, []);

  return (
    <TextField
      {...props}
      ref={inputRef}
      label={label}
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      multiline={multiline}
      rows={rows}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      variant="outlined"
    />
  );
});

// Isolated Switch Component to prevent accordion closing
const IsolatedSwitch = React.memo(({ checked, onChange, color = "primary", ...props }) => {
  const handleChange = useCallback((e) => {
    e.stopPropagation();
    if (onChange) {
      onChange(e);
    }
  }, [onChange]);

  return (
    <Switch
      {...props}
      checked={checked}
      onChange={handleChange}
      onClick={(e) => e.stopPropagation()}
      color={color}
    />
  );
});

// ─── Module-scope field components (stable identity across renders) ───
const NameField = React.memo(({ initialValue, onUpdate }) => (
  <FocusTextField fullWidth label="Full Name" value={initialValue || ''} onChange={useCallback((v) => onUpdate('name', v), [onUpdate])} variant="outlined" />
));
const TitleField = React.memo(({ initialValue, onUpdate }) => (
  <FocusTextField fullWidth label="Title/Position" value={initialValue || ''} onChange={useCallback((v) => onUpdate('title', v), [onUpdate])} variant="outlined" />
));
const CompanyField = React.memo(({ initialValue, onUpdate }) => (
  <FocusTextField fullWidth label="Company" value={initialValue || ''} onChange={useCallback((v) => onUpdate('company', v), [onUpdate])} variant="outlined" />
));
const EmailField = React.memo(({ initialValue, onUpdate }) => (
  <FocusTextField fullWidth label="Email" value={initialValue || ''} onChange={useCallback((v) => onUpdate('email', v), [onUpdate])} variant="outlined" />
));
const PhoneField = React.memo(({ initialValue, onUpdate }) => (
  <FocusTextField fullWidth label="Phone" value={initialValue || ''} onChange={useCallback((v) => onUpdate('phone', v), [onUpdate])} variant="outlined" />
));
const WebsiteField = React.memo(({ initialValue, onUpdate }) => (
  <FocusTextField fullWidth label="Website" value={initialValue || ''} onChange={useCallback((v) => onUpdate('website', v), [onUpdate])} variant="outlined" />
));

const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|bmp|svg)$/i;

function isLikelyImageFile(file) {
  if (!file) return false;
  if (file.type.startsWith('image/')) return true;
  return IMAGE_EXT_RE.test(file.name || '');
}

function firstImageFileFromFileList(list) {
  if (!list?.length) return null;
  return Array.from(list).find(isLikelyImageFile) || null;
}

const BasicInfoFieldsOuter = React.memo(({ cardData, setCardData }) => {
  const theme = useTheme();
  const [bannerCropOpen, setBannerCropOpen] = useState(false);
  const [bannerCropSrc, setBannerCropSrc] = useState(null);
  const [bannerEditLoading, setBannerEditLoading] = useState(false);
  const [bannerDropActive, setBannerDropActive] = useState(false);
  const [profileCropOpen, setProfileCropOpen] = useState(false);
  const [profileCropSrc, setProfileCropSrc] = useState(null);
  const [profileEditLoading, setProfileEditLoading] = useState(false);
  const [profileDropActive, setProfileDropActive] = useState(false);

  const handleFieldUpdate = useCallback((field, value) => {
    setCardData(prev => ({ ...prev, [field]: value }));
  }, [setCardData]);

  const closeProfileCrop = useCallback(() => {
    setProfileCropOpen(false);
    setProfileCropSrc((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const openProfileImageFromFile = useCallback((file) => {
    if (!file) return;
    if (!isLikelyImageFile(file)) {
      alert('Please choose an image file.');
      return;
    }
    setProfileCropSrc((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setProfileCropOpen(true);
  }, []);

  const handleProfileFilePick = useCallback((event) => {
    const file = event.target.files?.[0];
    if (event.target) event.target.value = '';
    openProfileImageFromFile(file);
  }, [openProfileImageFromFile]);

  const handleProfileDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleProfileDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setProfileDropActive(true);
  }, []);

  const handleProfileDragLeave = useCallback((e) => {
    e.preventDefault();
    if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
    setProfileDropActive(false);
  }, []);

  const handleProfileDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setProfileDropActive(false);
    const list = e.dataTransfer?.files;
    if (!list?.length) return;
    const file = firstImageFileFromFileList(list);
    if (!file) {
      alert('Please drop an image file.');
      return;
    }
    openProfileImageFromFile(file);
  }, [openProfileImageFromFile]);

  const handleEditExistingProfile = useCallback(async () => {
    const url = cardData.profileImageUrl;
    if (!url) return;
    setProfileEditLoading(true);
    try {
      const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      if (!blob.type.startsWith('image/')) throw new Error('not an image');
      setProfileCropSrc((prev) => {
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
      setProfileCropOpen(true);
    } catch (e) {
      console.error('Load profile image for edit:', e);
      alert('Could not load this photo for editing. Use "Change" to pick the file again.');
    } finally {
      setProfileEditLoading(false);
    }
  }, [cardData.profileImageUrl]);

  const uploadCroppedProfile = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('profileImage', file);
    const { data: result } = await api.post('/upload/profile-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const fullImageUrl = result?.imageUrl?.startsWith('http')
      ? result.imageUrl
      : result?.imageUrl
        ? `https://api.webcard.digital${result.imageUrl}`
        : '';
    if (!fullImageUrl) throw new Error('Upload failed');
    setCardData((prev) => ({ ...prev, profileImage: file, profileImageUrl: fullImageUrl }));
  }, [setCardData]);

  const closeBannerCrop = useCallback(() => {
    setBannerCropOpen(false);
    setBannerCropSrc((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const openBannerImageFromFile = useCallback((file) => {
    if (!file) return;
    if (!isLikelyImageFile(file)) {
      alert('Please choose an image file.');
      return;
    }
    setBannerCropSrc((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setBannerCropOpen(true);
  }, []);

  const handleBannerFilePick = useCallback((event) => {
    const file = event.target.files?.[0];
    if (event.target) event.target.value = '';
    openBannerImageFromFile(file);
  }, [openBannerImageFromFile]);

  const handleBannerDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleBannerDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setBannerDropActive(true);
  }, []);

  const handleBannerDragLeave = useCallback((e) => {
    e.preventDefault();
    if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
    setBannerDropActive(false);
  }, []);

  const handleBannerDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setBannerDropActive(false);
    const list = e.dataTransfer?.files;
    if (!list?.length) return;
    const file = firstImageFileFromFileList(list);
    if (!file) {
      alert('Please drop an image file.');
      return;
    }
    openBannerImageFromFile(file);
  }, [openBannerImageFromFile]);

  const handleEditExistingBanner = useCallback(async () => {
    const url = cardData.bannerImageUrl;
    if (!url) return;
    setBannerEditLoading(true);
    try {
      const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      if (!blob.type.startsWith('image/')) throw new Error('not an image');
      setBannerCropSrc((prev) => {
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
      setBannerCropOpen(true);
    } catch (e) {
      console.error('Load banner for edit:', e);
      alert('Could not load this banner for editing. Use "Change Banner" to pick the file again.');
    } finally {
      setBannerEditLoading(false);
    }
  }, [cardData.bannerImageUrl]);

  const uploadCroppedBanner = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('bannerImage', file);
    const { data: result } = await api.post('/upload/banner-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const fullImageUrl = result?.imageUrl?.startsWith('http')
      ? result.imageUrl
      : result?.imageUrl
        ? `https://api.webcard.digital${result.imageUrl}`
        : '';
    if (!fullImageUrl) throw new Error('Upload failed');
    setCardData((prev) => ({ ...prev, bannerImage: file, bannerImageUrl: fullImageUrl }));
  }, [setCardData]);

  const handleRemoveProfileImage = useCallback(() => {
    setCardData(prev => ({ ...prev, profileImage: null, profileImageUrl: '' }));
  }, [setCardData]);

  const handleRemoveBannerImage = useCallback(() => {
    setCardData(prev => ({ ...prev, bannerImage: null, bannerImageUrl: '' }));
  }, [setCardData]);

  const mediaActionBtnSx = {
    textTransform: 'none',
    fontSize: '0.8125rem',
    fontWeight: 500,
    px: 1.25,
    py: 0.375,
    minHeight: 32,
    borderRadius: 2,
    lineHeight: 1.2,
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>Profile Picture</Typography>
        <Box
          onDragOver={handleProfileDragOver}
          onDragEnter={handleProfileDragEnter}
          onDragLeave={handleProfileDragLeave}
          onDrop={handleProfileDrop}
          role="group"
          aria-label="Profile picture"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            alignItems: 'center',
            gap: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: profileDropActive
              ? 'action.hover'
              : theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.06)
                : theme.palette.grey[50],
            border: profileDropActive ? '2px dashed' : '1px solid',
            borderColor: profileDropActive ? 'primary.main' : 'divider',
            transition: 'background-color 0.15s ease, border-color 0.15s ease',
          }}
        >
          <Box sx={{ position: 'relative', flexShrink: 0, alignSelf: 'flex-start' }}>
            <Avatar
              src={cardData.profileImageUrl}
              sx={{
                width: 80,
                height: 80,
                border: '2px solid',
                borderColor: 'divider',
                boxShadow: theme.palette.mode === 'dark' ? '0 2px 12px rgba(0,0,0,0.45)' : '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              {cardData.name ? cardData.name.charAt(0).toUpperCase() : 'A'}
            </Avatar>
            {cardData.profileImageUrl ? (
              <Tooltip title="Remove profile photo">
                <IconButton
                  size="small"
                  onClick={handleRemoveProfileImage}
                  aria-label="Remove profile photo"
                  sx={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    width: 28,
                    height: 28,
                    p: 0,
                    zIndex: 1,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                    '&:hover': { bgcolor: 'error.main', color: 'error.contrastText', borderColor: 'error.main' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 0' }, minWidth: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
              <input accept="image/*" style={{ display: 'none' }} id="profile-image-upload" type="file" onChange={handleProfileFilePick} />
              <label htmlFor="profile-image-upload" style={{ display: 'inline-flex' }}>
                <Button
                  variant="contained"
                  component="span"
                  size="small"
                  startIcon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
                  sx={mediaActionBtnSx}
                >
                  {cardData.profileImageUrl ? 'Change' : 'Upload'}
                </Button>
              </label>
              {cardData.profileImageUrl ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={profileEditLoading ? <CircularProgress size={14} color="inherit" /> : <CropIcon sx={{ fontSize: 18 }} />}
                  onClick={handleEditExistingProfile}
                  disabled={profileEditLoading}
                  sx={mediaActionBtnSx}
                >
                  Crop
                </Button>
              ) : null}
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75, lineHeight: 1.4 }}>
              {PROFILE_EXPORT_WIDTH}×{PROFILE_EXPORT_HEIGHT}px recommended · JPG or PNG
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>Banner Image (Optional)</Typography>
        <Box
          onDragOver={handleBannerDragOver}
          onDragEnter={handleBannerDragEnter}
          onDragLeave={handleBannerDragLeave}
          onDrop={handleBannerDrop}
          role="group"
          aria-label="Banner image"
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: bannerDropActive
              ? 'action.hover'
              : theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.06)
                : theme.palette.grey[50],
            border: bannerDropActive ? '2px dashed' : '1px solid',
            borderColor: bannerDropActive ? 'primary.main' : 'divider',
            transition: 'background-color 0.15s ease, border-color 0.15s ease',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              alignItems: 'center',
              gap: 2,
            }}
          >
            {cardData.bannerImageUrl ? (
              <Box
                sx={{
                  position: 'relative',
                  flexShrink: 0,
                  alignSelf: 'flex-start',
                  width: { xs: '100%', sm: 168 },
                  maxWidth: 240,
                  overflow: 'visible',
                }}
              >
                <BannerStrip
                  imageUrl={cardData.bannerImageUrl}
                  fallbackBg="grey.200"
                  borderRadius={12}
                  sx={{
                    width: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  }}
                />
                <Tooltip title="Remove Banner">
                  <IconButton
                    size="small"
                    onClick={handleRemoveBannerImage}
                    aria-label="Remove banner"
                    sx={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      width: 28,
                      height: 28,
                      p: 0,
                      zIndex: 1,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                      '&:hover': { bgcolor: 'error.main', color: 'error.contrastText', borderColor: 'error.main' },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Box
                sx={{
                  width: { xs: '100%', sm: 168 },
                  maxWidth: 240,
                  aspectRatio: `${BANNER_EXPORT_WIDTH} / ${BANNER_EXPORT_HEIGHT}`,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  flexShrink: 0,
                }}
              >
                No banner
              </Box>
            )}
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 0' }, minWidth: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                <input accept="image/*" style={{ display: 'none' }} id="banner-image-upload" type="file" onChange={handleBannerFilePick} />
                <label htmlFor="banner-image-upload" style={{ display: 'inline-flex' }}>
                  <Button
                    variant="contained"
                    component="span"
                    size="small"
                    startIcon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
                    sx={mediaActionBtnSx}
                  >
                    {cardData.bannerImageUrl ? 'Change' : 'Upload'}
                  </Button>
                </label>
                {cardData.bannerImageUrl ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={bannerEditLoading ? <CircularProgress size={14} color="inherit" /> : <CropIcon sx={{ fontSize: 18 }} />}
                    onClick={handleEditExistingBanner}
                    disabled={bannerEditLoading}
                    sx={mediaActionBtnSx}
                  >
                    Crop
                  </Button>
                ) : null}
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75, lineHeight: 1.4 }}>
                {BANNER_EXPORT_WIDTH}×{BANNER_EXPORT_HEIGHT}px recommended · JPG or PNG
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <BannerCropDialog
        open={profileCropOpen}
        imageSrc={profileCropSrc || ''}
        onClose={closeProfileCrop}
        onApply={uploadCroppedProfile}
        variant="profile"
      />
      <BannerCropDialog
        open={bannerCropOpen}
        imageSrc={bannerCropSrc || ''}
        onClose={closeBannerCrop}
        onApply={uploadCroppedBanner}
      />
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>Personal Information</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}><NameField key="name-field" initialValue={cardData.name} onUpdate={handleFieldUpdate} /></Grid>
          <Grid item xs={12} sm={6}><TitleField key="title-field" initialValue={cardData.title} onUpdate={handleFieldUpdate} /></Grid>
          <Grid item xs={12}><CompanyField key="company-field" initialValue={cardData.company} onUpdate={handleFieldUpdate} /></Grid>
          <Grid item xs={12} sm={6}><EmailField key="email-field" initialValue={cardData.email} onUpdate={handleFieldUpdate} /></Grid>
          <Grid item xs={12} sm={6}><PhoneField key="phone-field" initialValue={cardData.phone} onUpdate={handleFieldUpdate} /></Grid>
          <Grid item xs={12}><WebsiteField key="website-field" initialValue={cardData.website} onUpdate={handleFieldUpdate} /></Grid>
        </Grid>
      </Box>
    </Box>
  );
});

const AboutSectionFieldOuter = React.memo(({ cardData, setCardData }) => {
  const handleAboutChange = useCallback((value) => {
    setCardData(prev => ({ ...prev, about: value }));
  }, [setCardData]);
  return (
    <AboutMuiTiptapField
      fullWidth
      label="About"
      rows={4}
      value={cardData.about || ''}
      onChange={handleAboutChange}
      placeholder="Tell people about yourself..."
    />
  );
});

const SectionItemOuter = React.memo(({ section, expandedSections, setExpandedSections, onSectionToggle, onRemoveSection, onDragHandleStart, cardData, setCardData, socialLinks, setSocialLinks, onRemoveSocialLink, onAddSocialLink, customLinks, setCustomLinks, onRemoveCustomLink, onAddCustomLink, customSections, setCustomSections, gallerySection, setGallerySection, galleryUploading = false, setGalleryUploading = () => {}, setToast = () => {} }) => {
  const theme = useTheme();
  const isExpanded = expandedSections[section.id] || false;

  const reorderGalleryImage = useCallback((fromIndex, delta) => {
    setGallerySection((prev) => {
      const arr = [...(prev.images || [])];
      const to = fromIndex + delta;
      if (to < 0 || to >= arr.length) return prev;
      const next = [...arr];
      [next[fromIndex], next[to]] = [next[to], next[fromIndex]];
      return { ...prev, images: next };
    });
  }, [setGallerySection]);

  const reorderGalleryImageLegacy = useCallback((sectionId, fromIndex, delta) => {
    setCardData((prev) => {
      const key = `${sectionId}_images`;
      const arr = [...(prev[key] || [])];
      const to = fromIndex + delta;
      if (to < 0 || to >= arr.length) return prev;
      const next = [...arr];
      [next[fromIndex], next[to]] = [next[to], next[fromIndex]];
      return { ...prev, [key]: next };
    });
  }, [setCardData]);

  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    setExpandedSections(prev => ({ ...prev, [section.id]: !prev[section.id] }));
  }, [section.id, setExpandedSections]);

  const handleSectionToggleClick = useCallback((e) => { e.stopPropagation(); onSectionToggle(section.id); }, [section.id, onSectionToggle]);
  const handleRemove = useCallback((e) => { e.stopPropagation(); onRemoveSection(section.id); }, [section.id, onRemoveSection]);

  const sectionHeaderBg = section.enabled
    ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.1)
    : theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.06)
      : theme.palette.grey[100];
  const sectionHeaderHoverBg = section.enabled
    ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.16)
    : theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.1)
      : theme.palette.grey[200];

  return (
    <Card sx={{ mb: 2, borderRadius: 1 }}>
      <Box sx={{ p: 2, bgcolor: sectionHeaderBg, '&:hover': { bgcolor: sectionHeaderHoverBg }, cursor: 'pointer' }} onClick={handleToggle}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          {section.id === 'basic-info' ? (
            <Box
              component="span"
              onClick={(e) => e.stopPropagation()}
              aria-hidden
              sx={{
                display: 'inline-flex',
                mr: 2,
                cursor: 'default',
                color: 'text.disabled',
                alignItems: 'center',
              }}
            >
              <LockOutlinedIcon fontSize="small" />
            </Box>
          ) : (
            <Box
              component="span"
              draggable
              onDragStart={(e) => {
                e.stopPropagation();
                if (onDragHandleStart) onDragHandleStart(e);
              }}
              onClick={(e) => e.stopPropagation()}
              aria-hidden
              sx={{
                display: 'inline-flex',
                mr: 2,
                cursor: 'grab',
                color: 'text.secondary',
                alignItems: 'center',
                touchAction: 'none',
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <DragIcon />
            </Box>
          )}
          <FormControlLabel control={<IsolatedSwitch checked={section.enabled} onChange={handleSectionToggleClick} color="primary" />} label="" sx={{ mr: 2 }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>{section.name}</Typography>
          {!LOCKED_SECTION_IDS.has(section.id) && (
            <IconButton onClick={handleRemove} sx={{ color: 'error.main' }} aria-label={`Remove ${section.name}`}><DeleteIcon /></IconButton>
          )}
          <IconButton onClick={handleToggle} sx={{ color: 'text.secondary' }}><ExpandMoreIcon sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }} /></IconButton>
        </Box>
      </Box>
      {isExpanded && (
        <Box sx={{ p: 2 }}>
          {section.id === 'basic-info' && <Box onClick={(e) => e.stopPropagation()}><BasicInfoFieldsOuter cardData={cardData} setCardData={setCardData} /></Box>}
          {section.id === 'about' && <Box onClick={(e) => e.stopPropagation()}><AboutSectionFieldOuter cardData={cardData} setCardData={setCardData} /></Box>}
          {section.id === 'social' && (
            <Box onClick={(e) => e.stopPropagation()}>
              {socialLinks.map((link) => (
                <Box key={link.id} sx={{ display: 'flex', alignItems: 'center', mb: 2 }} onClick={(e) => e.stopPropagation()}>
                  <FormControlLabel control={<IsolatedSwitch checked={link.enabled} onChange={(e) => { setSocialLinks(prev => prev.map(l => l.id === link.id ? { ...l, enabled: e.target.checked } : l)); }} color="primary" />} label="" sx={{ mr: 1 }} />
                  <FocusTextField label="Platform" value={link.platform} onChange={(value) => { setSocialLinks(prev => prev.map(l => l.id === link.id ? { ...l, platform: value } : l)); }} sx={{ mr: 2, width: 120 }} />
                  <FocusTextField label="URL" value={link.url} onChange={(value) => { setSocialLinks(prev => prev.map(l => l.id === link.id ? { ...l, url: value } : l)); }} sx={{ flex: 1, mr: 2 }} />
                  <IconButton onClick={(e) => { e.stopPropagation(); onRemoveSocialLink(link.id); }} sx={{ color: 'error.main' }}><DeleteIcon /></IconButton>
                </Box>
              ))}
              <Button startIcon={<AddIcon />} onClick={(e) => { e.stopPropagation(); onAddSocialLink(); }} variant="outlined" size="small">Add Social Link</Button>
            </Box>
          )}
          {section.id === 'contact' && (
            <Grid container spacing={2} onClick={(e) => e.stopPropagation()}>
              <Grid item xs={12} sm={6}><FocusTextField fullWidth label="WhatsApp Number" value={cardData.whatsapp || ''} onChange={(value) => setCardData(prev => ({ ...prev, whatsapp: value }))} placeholder="+91 98765 43210" helperText="With country code. Opens WhatsApp chat on tap." /></Grid>
              <Grid item xs={12} sm={6}><FocusTextField fullWidth label="Additional Phone" value={cardData.additionalPhone || ''} onChange={(value) => setCardData(prev => ({ ...prev, additionalPhone: value }))} placeholder="+91 98765 43210" /></Grid>
              <Grid item xs={12} sm={6}><FocusTextField fullWidth label="Address" value={cardData.address || ''} onChange={(value) => setCardData(prev => ({ ...prev, address: value }))} placeholder="123 Main St, City, State 12345" /></Grid>
              <Grid item xs={12} sm={6}><FocusTextField fullWidth label="City" value={cardData.city || ''} onChange={(value) => setCardData(prev => ({ ...prev, city: value }))} placeholder="Mumbai" /></Grid>
              <Grid item xs={12} sm={6}><FocusTextField fullWidth label="State/Province" value={cardData.state || ''} onChange={(value) => setCardData(prev => ({ ...prev, state: value }))} placeholder="Maharashtra" /></Grid>
              <Grid item xs={12} sm={6}><FocusTextField fullWidth label="Country" value={cardData.country || ''} onChange={(value) => setCardData(prev => ({ ...prev, country: value }))} placeholder="India" /></Grid>
              <Grid item xs={12}><FocusTextField fullWidth label="Google Maps Link" value={cardData.googleMapsUrl || ''} onChange={(value) => setCardData(prev => ({ ...prev, googleMapsUrl: value }))} placeholder="https://maps.google.com/..." helperText="Paste your Google Maps share link" /></Grid>
              <Grid item xs={12} sm={6}><FocusTextField fullWidth label="Fax" value={cardData.fax || ''} onChange={(value) => setCardData(prev => ({ ...prev, fax: value }))} placeholder="+91 22 1234 5678" /></Grid>
              <Grid item xs={12} sm={6}><FocusTextField fullWidth label="Office Hours" value={cardData.officeHours || ''} onChange={(value) => setCardData(prev => ({ ...prev, officeHours: value }))} placeholder="Mon - Fri: 9 AM - 5 PM" /></Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>Payment Links</Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>Add your UPI / payment IDs so visitors can pay you directly</Typography>
              </Grid>
              <Grid item xs={12} sm={6}><FocusTextField fullWidth label="UPI ID" value={cardData.upiId || ''} onChange={(value) => setCardData(prev => ({ ...prev, upiId: value }))} placeholder="yourname@upi" helperText="e.g. name@okicici, name@ybl" /></Grid>
              <Grid item xs={12} sm={6}><FocusTextField fullWidth label="GPay Number / UPI" value={cardData.gpay || ''} onChange={(value) => setCardData(prev => ({ ...prev, gpay: value }))} placeholder="+91 98765 43210 or UPI ID" /></Grid>
              <Grid item xs={12} sm={6}><FocusTextField fullWidth label="PhonePe Number / UPI" value={cardData.phonepe || ''} onChange={(value) => setCardData(prev => ({ ...prev, phonepe: value }))} placeholder="+91 98765 43210 or UPI ID" /></Grid>
              <Grid item xs={12} sm={6}><FocusTextField fullWidth label="Paytm Number / UPI" value={cardData.paytm || ''} onChange={(value) => setCardData(prev => ({ ...prev, paytm: value }))} placeholder="+91 98765 43210 or UPI ID" /></Grid>
            </Grid>
          )}
          {section.id === 'appointment' && (
            <Grid container spacing={2}>
              <Grid item xs={12}><FocusTextField fullWidth label="Calendar URL" value={cardData.calendarUrl || ''} onChange={(value) => setCardData(prev => ({ ...prev, calendarUrl: value }))} placeholder="https://calendly.com/yourname" /></Grid>
              <Grid item xs={12}><FocusTextField fullWidth label="Button Text" value={cardData.appointmentText || 'Book Appointment'} onChange={(value) => setCardData(prev => ({ ...prev, appointmentText: value }))} /></Grid>
            </Grid>
          )}
          {section.id === 'custom-sections' && (
            <Grid container spacing={2}>
              {customSections.map((block, index) => (
                <Grid item xs={12} key={index}>
                  <Card sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <IsolatedSwitch
                              checked={block.enabled !== false}
                              onChange={(e) => {
                                const next = [...customSections];
                                next[index] = { ...next[index], enabled: e.target.checked };
                                setCustomSections(next);
                              }}
                              color="primary"
                            />
                          }
                          label="Show on card"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ textAlign: { sm: 'right' } }}>
                        <IconButton
                          color="error"
                          onClick={() => setCustomSections((prev) => prev.filter((_, i) => i !== index))}
                          aria-label="Remove block"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                      <Grid item xs={12}>
                        <FocusTextField
                          fullWidth
                          label="Title"
                          value={block.title || ''}
                          onChange={(value) => {
                            const next = [...customSections];
                            next[index] = { ...next[index], title: value };
                            setCustomSections(next);
                          }}
                          placeholder="Section title"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FocusTextField
                          fullWidth
                          label="Description"
                          value={block.description || ''}
                          onChange={(value) => {
                            const next = [...customSections];
                            next[index] = { ...next[index], description: value };
                            setCustomSections(next);
                          }}
                          multiline
                          rows={3}
                          placeholder="Content"
                        />
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setCustomSections((prev) => [...prev, { enabled: true, title: '', description: '' }])}
                  variant="outlined"
                  sx={{ width: '100%' }}
                >
                  Add custom block
                </Button>
              </Grid>
            </Grid>
          )}
          {section.id === 'custom-links' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>Custom Links</Typography>
                <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>Links appear on your card in order</Typography>
              </Grid>
              {customLinks.map((link) => (
                <Grid item xs={12} key={link.id}>
                  <Card sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <IsolatedSwitch
                              checked={link.enabled}
                              onChange={(e) => {
                                setCustomLinks((prev) => prev.map((l) => (l.id === link.id ? { ...l, enabled: e.target.checked } : l)));
                              }}
                              color="primary"
                            />
                          }
                          label="Enabled"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}><FocusTextField fullWidth label="Title" value={link.title || ''} onChange={(value) => { setCustomLinks((prev) => prev.map((l) => (l.id === link.id ? { ...l, title: value } : l))); }} /></Grid>
                      <Grid item xs={12} sm={5}><FocusTextField fullWidth label="URL" value={link.url || ''} onChange={(value) => { setCustomLinks((prev) => prev.map((l) => (l.id === link.id ? { ...l, url: value } : l))); }} /></Grid>
                      <Grid item xs={12} sm={1}>
                        <IconButton onClick={() => onRemoveCustomLink(link.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Button startIcon={<AddIcon />} onClick={onAddCustomLink} variant="outlined" sx={{ width: '100%' }}>
                  Add link
                </Button>
              </Grid>
            </Grid>
          )}
          {section.id === 'gallery' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>Image Gallery</Typography>
              </Grid>
              {(gallerySection.images || []).map((image, index) => {
                const galleryList = gallerySection.images || [];
                const galleryLen = galleryList.length;
                return (
                  <Grid item xs={12} key={index}>
                    <Card sx={{ p: 1.5, display: 'flex', alignItems: 'flex-start', gap: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Box
                        component="img"
                        src={image.url}
                        alt={image.title || ''}
                        sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }} noWrap title={galleryFilenameFromUrl(image.url)}>
                          {galleryFilenameFromUrl(image.url)}
                        </Typography>
                        <FocusTextField
                          fullWidth
                          label="Image Title"
                          value={image.title || ''}
                          onChange={(value) => {
                            const ni = [...(gallerySection.images || [])];
                            ni[index] = { ...ni[index], title: value };
                            setGallerySection((prev) => ({ ...prev, images: ni }));
                          }}
                          placeholder="Image title"
                          size="small"
                        />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, gap: 0.25 }}>
                        <IconButton size="small" onClick={() => reorderGalleryImage(index, -1)} disabled={index === 0} aria-label="Move image up">
                          <KeyboardArrowUpIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => reorderGalleryImage(index, 1)} disabled={index === galleryLen - 1} aria-label="Move image down">
                          <KeyboardArrowDownIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            const ni = [...(gallerySection.images || [])];
                            ni.splice(index, 1);
                            setGallerySection((prev) => ({ ...prev, images: ni }));
                          }}
                          aria-label="Remove image"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
              {(gallerySection.images || []).length > 2 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>View Type</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                    {GALLERY_VIEW_TYPES.map((type) => {
                      const selected = (gallerySection.layout || 'grid5') === type;
                      return (
                        <Card
                          key={type}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setGallerySection((p) => ({ ...p, layout: type }));
                            }
                          }}
                          onClick={() => setGallerySection((p) => ({ ...p, layout: type }))}
                          sx={{
                            width: 88,
                            cursor: 'pointer',
                            p: 1,
                            textAlign: 'center',
                            border: 2,
                            borderColor: selected ? 'primary.main' : 'divider',
                            bgcolor: selected ? `${theme.palette.primary.main}14` : 'background.paper',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.75,
                            '&:hover': { borderColor: 'primary.light' },
                          }}
                        >
                          <GalleryLayoutIcon type={type} active={selected} />
                          <Typography variant="caption" sx={{ fontWeight: selected ? 600 : 400, lineHeight: 1.2, fontSize: '0.65rem' }}>
                            {GALLERY_LAYOUT_LABELS[type]}
                          </Typography>
                        </Card>
                      );
                    })}
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Button
                  startIcon={galleryUploading ? <CircularProgress size={18} /> : <CloudUploadIcon />}
                  disabled={galleryUploading || (gallerySection.images || []).length >= MAX_GALLERY_IMAGES}
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.multiple = true;
                    input.onchange = async (e) => {
                      const files = Array.from(e.target.files);
                      if (!files.length) return;
                      const currentCount = (gallerySection.images || []).length;
                      const slotsRemaining = Math.max(0, MAX_GALLERY_IMAGES - currentCount);
                      if (slotsRemaining <= 0) {
                        setToast({ open: true, message: `You can add up to ${MAX_GALLERY_IMAGES} images per gallery.`, severity: 'warning' });
                        return;
                      }
                      const oversize = files.filter((f) => f.size > 5 * 1024 * 1024);
                      if (oversize.length) {
                        setToast({ open: true, message: `${oversize.length} file(s) exceed 5 MB limit and were skipped.`, severity: 'warning' });
                      }
                      let valid = files.filter((f) => f.size <= 5 * 1024 * 1024);
                      if (!valid.length) return;
                      if (valid.length > slotsRemaining) {
                        setToast({
                          open: true,
                          message: `Only ${slotsRemaining} more image(s) allowed (${MAX_GALLERY_IMAGES} max per gallery). Extra files were not uploaded.`,
                          severity: 'warning',
                        });
                        valid = valid.slice(0, slotsRemaining);
                      }
                      setGalleryUploading(true);
                      const uploaded = [];
                      try {
                        for (let i = 0; i < valid.length; i++) {
                          const formData = new FormData();
                          formData.append('galleryImages', valid[i]);
                          const { data: result } = await api.post('/upload/gallery-images', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                          if (result.images?.length) uploaded.push(...result.images);
                        }
                        if (uploaded.length) {
                          setGallerySection((prev) => ({
                            ...prev,
                            images: [...(prev.images || []), ...uploaded],
                          }));
                        }
                      } catch (err) {
                        const msg =
                          err.response?.data?.message ||
                          (err.response?.status === 413 ? 'File too large. Use images under 5 MB each.' : 'Upload failed. Please try again.');
                        setToast({ open: true, message: msg, severity: 'error' });
                      } finally {
                        setGalleryUploading(false);
                      }
                    };
                    input.click();
                  }}
                  variant="outlined"
                  sx={{ width: '100%' }}
                >
                  {galleryUploading ? 'Uploading...' : 'Upload Images'}
                </Button>
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
                  Up to {MAX_GALLERY_IMAGES} images · Max 5 MB per image · Select multiple; each uploads one at a time
                </Typography>
              </Grid>
            </Grid>
          )}
          {section.id.startsWith('custom-') && section.id !== 'custom-sections' && section.id !== 'custom-links' && (
            <Grid container spacing={2}>
              <Grid item xs={12}><FocusTextField fullWidth label="Section Header" value={cardData[`${section.id}_header`] || ''} onChange={(value) => setCardData(prev => ({ ...prev, [`${section.id}_header`]: value }))} placeholder="Enter section title" /></Grid>
              <Grid item xs={12}><FocusTextField fullWidth label="Section Description" value={cardData[`${section.id}_description`] || ''} onChange={(value) => setCardData(prev => ({ ...prev, [`${section.id}_description`]: value }))} multiline rows={3} placeholder="Enter section description or content" /></Grid>
            </Grid>
          )}
          {section.id.startsWith('links-') && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>Custom Links (legacy)</Typography>
                <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>Save the card to migrate into the new Custom Links list</Typography>
              </Grid>
              {cardData[`${section.id}_links`]?.map((link, index) => (
                <Grid item xs={12} key={index}>
                  <Card sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}><FocusTextField fullWidth label="Link Title" value={link.title || ''} onChange={(value) => { const nl = [...(cardData[`${section.id}_links`] || [])]; nl[index] = { ...nl[index], title: value }; setCardData(prev => ({ ...prev, [`${section.id}_links`]: nl })); }} placeholder="e.g., Credit Cards, Demat" /></Grid>
                      <Grid item xs={12} sm={5}><FocusTextField fullWidth label="URL" value={link.url || ''} onChange={(value) => { const nl = [...(cardData[`${section.id}_links`] || [])]; nl[index] = { ...nl[index], url: value }; setCardData(prev => ({ ...prev, [`${section.id}_links`]: nl })); }} placeholder="https://example.com" /></Grid>
                      <Grid item xs={12} sm={1}><IconButton onClick={() => { const nl = [...(cardData[`${section.id}_links`] || [])]; nl.splice(index, 1); setCardData(prev => ({ ...prev, [`${section.id}_links`]: nl })); }} color="error"><DeleteIcon /></IconButton></Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))}
              <Grid item xs={12}><Button startIcon={<AddIcon />} onClick={() => { const nl = [...(cardData[`${section.id}_links`] || []), { title: '', url: '' }]; setCardData(prev => ({ ...prev, [`${section.id}_links`]: nl })); }} variant="outlined" sx={{ width: '100%' }}>Add Link</Button></Grid>
            </Grid>
          )}
          {section.id.startsWith('images-') && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>Image Gallery (legacy)</Typography>
              </Grid>
              {(cardData[`${section.id}_images`] || []).map((image, index) => {
                const galleryList = cardData[`${section.id}_images`] || [];
                const galleryLen = galleryList.length;
                return (
                  <Grid item xs={12} key={index}>
                    <Card sx={{ p: 1.5, display: 'flex', alignItems: 'flex-start', gap: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Box component="img" src={image.url} alt={image.title || ''} sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }} noWrap title={galleryFilenameFromUrl(image.url)}>
                          {galleryFilenameFromUrl(image.url)}
                        </Typography>
                        <FocusTextField
                          fullWidth
                          label="Image Title"
                          value={image.title || ''}
                          onChange={(value) => {
                            const ni = [...(cardData[`${section.id}_images`] || [])];
                            ni[index] = { ...ni[index], title: value };
                            setCardData((prev) => ({ ...prev, [`${section.id}_images`]: ni }));
                          }}
                          placeholder="Image title"
                          size="small"
                        />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, gap: 0.25 }}>
                        <IconButton size="small" onClick={() => reorderGalleryImageLegacy(section.id, index, -1)} disabled={index === 0} aria-label="Move image up">
                          <KeyboardArrowUpIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => reorderGalleryImageLegacy(section.id, index, 1)} disabled={index === galleryLen - 1} aria-label="Move image down">
                          <KeyboardArrowDownIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            const ni = [...(cardData[`${section.id}_images`] || [])];
                            ni.splice(index, 1);
                            setCardData((prev) => ({ ...prev, [`${section.id}_images`]: ni }));
                          }}
                          aria-label="Remove image"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
              {((cardData[`${section.id}_images`] || []).length > 2) && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>View Type</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                    {GALLERY_VIEW_TYPES.map((type) => {
                      const selected = (cardData[`${section.id}_galleryLayout`] || 'grid5') === type;
                      return (
                        <Card
                          key={type}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setCardData((p) => ({ ...p, [`${section.id}_galleryLayout`]: type }));
                            }
                          }}
                          onClick={() => setCardData((p) => ({ ...p, [`${section.id}_galleryLayout`]: type }))}
                          sx={{
                            width: 88,
                            cursor: 'pointer',
                            p: 1,
                            textAlign: 'center',
                            border: 2,
                            borderColor: selected ? 'primary.main' : 'divider',
                            bgcolor: selected ? `${theme.palette.primary.main}14` : 'background.paper',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.75,
                            '&:hover': { borderColor: 'primary.light' },
                          }}
                        >
                          <GalleryLayoutIcon type={type} active={selected} />
                          <Typography variant="caption" sx={{ fontWeight: selected ? 600 : 400, lineHeight: 1.2, fontSize: '0.65rem' }}>
                            {GALLERY_LAYOUT_LABELS[type]}
                          </Typography>
                        </Card>
                      );
                    })}
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Button
                  startIcon={galleryUploading ? <CircularProgress size={18} /> : <CloudUploadIcon />}
                  disabled={galleryUploading || ((cardData[`${section.id}_images`] || []).length >= MAX_GALLERY_IMAGES)}
                  onClick={() => {
                    const input = document.createElement('input'); input.type = 'file'; input.accept = 'image/*'; input.multiple = true;
                    input.onchange = async (e) => {
                      const files = Array.from(e.target.files);
                      if (!files.length) return;
                      const currentCount = (cardData[`${section.id}_images`] || []).length;
                      const slotsRemaining = Math.max(0, MAX_GALLERY_IMAGES - currentCount);
                      if (slotsRemaining <= 0) {
                        setToast({ open: true, message: `You can add up to ${MAX_GALLERY_IMAGES} images per gallery.`, severity: 'warning' });
                        return;
                      }
                      const oversize = files.filter(f => f.size > 5 * 1024 * 1024);
                      if (oversize.length) {
                        setToast({ open: true, message: `${oversize.length} file(s) exceed 5 MB limit and were skipped.`, severity: 'warning' });
                      }
                      let valid = files.filter(f => f.size <= 5 * 1024 * 1024);
                      if (!valid.length) return;
                      if (valid.length > slotsRemaining) {
                        setToast({
                          open: true,
                          message: `Only ${slotsRemaining} more image(s) allowed (${MAX_GALLERY_IMAGES} max per gallery). Extra files were not uploaded.`,
                          severity: 'warning',
                        });
                        valid = valid.slice(0, slotsRemaining);
                      }
                      setGalleryUploading(true);
                      const uploaded = [];
                      try {
                        for (let i = 0; i < valid.length; i++) {
                          const formData = new FormData();
                          formData.append('galleryImages', valid[i]);
                          const { data: result } = await api.post('/upload/gallery-images', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                          if (result.images?.length) uploaded.push(...result.images);
                        }
                        if (uploaded.length) {
                          const ni = [...(cardData[`${section.id}_images`] || []), ...uploaded];
                          setCardData(prev => ({ ...prev, [`${section.id}_images`]: ni }));
                        }
                      } catch (err) {
                        const msg = err.response?.data?.message || (err.response?.status === 413 ? 'File too large. Use images under 5 MB each.' : 'Upload failed. Please try again.');
                        setToast({ open: true, message: msg, severity: 'error' });
                      } finally {
                        setGalleryUploading(false);
                      }
                    };
                    input.click();
                  }}
                  variant="outlined"
                  sx={{ width: '100%' }}
                >
                  {galleryUploading ? 'Uploading...' : 'Upload Images'}
                </Button>
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
                  Up to {MAX_GALLERY_IMAGES} images · Max 5 MB per image · Select multiple; each uploads one at a time
                </Typography>
              </Grid>
            </Grid>
          )}
        </Box>
      )}
    </Card>
  );
});

const CardCustomizer = () => {
  const theme = useTheme();
  const editorCardShadow = theme.palette.mode === 'dark' ? 'none' : '0 4px 12px rgba(0,0,0,0.1)';
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const templateParam = searchParams.get('template') || '1';
  const [templateId, setTemplateId] = useState(null);
  const editCardId = searchParams.get('edit');
  const isEditMode = !!editCardId;
  const [template, setTemplate] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const [urlError, setUrlError] = useState('');
  const [checkingUrl, setCheckingUrl] = useState(false);
  const [urlStatus, setUrlStatus] = useState(''); // 'valid', 'invalid', 'checking'
  const [editLoadError, setEditLoadError] = useState('');

  // Fetch template info using the list endpoint (reliable) with single-item fallback
  const fetchTemplateInfo = async () => {
    try {
      setLoadingTemplate(true);
      const numericId = !isNaN(templateParam) && Number.isInteger(Number(templateParam))
        ? parseInt(templateParam)
        : null;

      const { data } = await api.get('/templates');
      if (data?.success && Array.isArray(data.templates)) {
        const found = data.templates.find(t =>
          (numericId && t.id === numericId) ||
          t.name === templateParam ||
          t.id?.toString() === templateParam
        );
        if (found) {
          setTemplateId(found.id);
          setTemplate({ id: found.id, name: found.name, description: found.description || '', category: found.category || 'Professional' });
          return;
        }
      }

      // Try single-item endpoint as fallback
      if (numericId) {
        try {
          const { data: single } = await api.get(`/templates/${numericId}`);
          if (single?.template) {
            setTemplateId(single.template.id);
            setTemplate(single.template);
            return;
          }
        } catch (_) { /* ignore */ }
      }

      setTemplateId(1);
      setTemplate({ id: 1, name: 'Classic Professional', description: 'Default template', category: 'Professional' });
    } catch (error) {
      console.error('Error fetching template info:', error);
      setTemplateId(1);
      setTemplate({ id: 1, name: 'Classic Professional', description: 'Default template', category: 'Professional' });
    } finally {
      setLoadingTemplate(false);
    }
  };

  // Fetch template on mount
  useEffect(() => {
    fetchTemplateInfo();
  }, [templateParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const [cardData, setCardData] = useState({
    customUrl: '',
    name: '',
    title: '',
    company: '',
    email: '',
    phone: '',
    website: '',
    about: '',
    profileImage: null,
    profileImageUrl: '',
    bannerImage: null,
    bannerImageUrl: '',
  });


  const [sections, setSections] = useState(() => DEFAULT_CARD_SECTIONS.map((s) => ({ ...s })));

  // Persistent accordion state - won't reset on re-renders
  const [expandedSections, setExpandedSections] = useState({
    'basic-info': true,
    about: false,
    social: false,
    contact: false,
    appointment: false,
    'custom-sections': false,
    'custom-links': false,
    gallery: false,
  });

  const [socialLinks, setSocialLinks] = useState([
    { id: 1, platform: 'LinkedIn', url: '', enabled: true },
    { id: 2, platform: 'Twitter', url: '', enabled: true },
    { id: 3, platform: 'Instagram', url: '', enabled: true },
    { id: 4, platform: 'Facebook', url: '', enabled: true },
  ]);

  const [customLinks, setCustomLinks] = useState([
    { id: 1, title: 'Portfolio', url: '', enabled: true },
    { id: 2, title: 'Resume', url: '', enabled: true },
  ]);

  const [customSections, setCustomSections] = useState([]);
  const [gallerySection, setGallerySection] = useState({
    enabled: true,
    layout: 'grid6',
    images: [],
  });

  useEffect(() => {
    // Load existing card data if in edit mode
    if (isEditMode && editCardId) {
      const loadCardData = async () => {
        setEditLoadError('');
        const slug = encodeURIComponent(editCardId);
        let data;
        try {
          ({ data } = await api.get(`/cards/owner/url/${slug}`));
        } catch (ownerErr) {
          try {
            ({ data } = await api.get(`/cards/url/${slug}`));
          } catch {
            console.error('Error loading card data:', ownerErr);
            setEditLoadError('Could not load this card. Check that you are signed in and the URL is correct.');
            return;
          }
        }
        const raw = data?.card?.cardData || data?.cardData;
        if (!raw) {
          setEditLoadError('This card has no saved data.');
          return;
        }

        let model;
        try {
          model = parseCardFromApi(raw);
        } catch (e) {
          console.error('parseCardFromApi:', e);
          setEditLoadError('Could not read this card’s saved data. Please contact support if this continues.');
          return;
        }

        setCardData((prev) => ({
          ...prev,
          ...model.cardData,
          profileImage: null,
          bannerImage: null,
          name: model.cardData.name || '',
          email: model.cardData.email || '',
          customUrl: editCardId,
          title: model.cardData.title || '',
          company: model.cardData.company || '',
          phone: model.cardData.phone || '',
          website: model.cardData.website || '',
          about: model.cardData.about || '',
          address: model.cardData.address || '',
          city: model.cardData.city || '',
          state: model.cardData.state || '',
          country: model.cardData.country || '',
          additionalPhone: model.cardData.additionalPhone || '',
          whatsapp: model.cardData.whatsapp || '',
          googleMapsUrl: model.cardData.googleMapsUrl || '',
          upiId: model.cardData.upiId || '',
          gpay: model.cardData.gpay || '',
          phonepe: model.cardData.phonepe || '',
          paytm: model.cardData.paytm || '',
          fax: model.cardData.fax || '',
          officeHours: model.cardData.officeHours || '',
          profileImageUrl: model.cardData.profileImageUrl
            ? model.cardData.profileImageUrl.startsWith('http')
              ? model.cardData.profileImageUrl
              : `https://api.webcard.digital${model.cardData.profileImageUrl}`
            : '',
          bannerImageUrl: model.cardData.bannerImageUrl
            ? model.cardData.bannerImageUrl.startsWith('http')
              ? model.cardData.bannerImageUrl
              : `https://api.webcard.digital${model.cardData.bannerImageUrl}`
            : '',
        }));

        const loaded = [...model.sections];
        const hasBasic = loaded.some((s) => s.id === 'basic-info');
        let merged = hasBasic
          ? loaded
          : [
              {
                id: 'basic-info',
                name: 'Basic Information',
                enabled: true,
                order: 0,
                fields: ['name', 'title', 'company', 'email', 'phone'],
              },
              ...loaded,
            ];
        {
          const sorted = sortCardSections(merged);
          const basic = sorted.find((s) => s.id === 'basic-info');
          const rest = sorted.filter((s) => s.id !== 'basic-info');
          merged = (basic ? [basic, ...rest] : rest).map((s, i) => ({ ...s, order: i + 1 }));
        }
        setSections(merged);
        setSocialLinks(model.socialLinks);
        setCustomLinks(model.customLinks);
        setCustomSections(model.customSections);
        setGallerySection(model.gallerySection);

        const tplId = data?.card?.templateId;
        const tplName = data?.card?.templateName;
        if (tplId != null && Number(tplId) > 0) {
          const idNum = Number(tplId);
          setTemplateId(idNum);
          setTemplate((prev) => ({
            id: idNum,
            name: tplName || prev?.name || 'Template',
            description: prev?.description || '',
            category: prev?.category || 'Professional',
          }));
        }
        setUrlStatus('valid');
        setUrlError('');
      };

      loadCardData();
    } else if (user) {
      // Generate a unique URL slug for new cards
      const base = (user.firstName || 'card').toLowerCase().replace(/[^a-z0-9]/g, '');
      const suffix = Math.random().toString(36).slice(2, 7);
      const generatedUrl = `${base}-${suffix}`;

      setCardData(prev => ({
        ...prev,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Your Name',
        email: user.email || '',
        customUrl: generatedUrl,
        title: user.title || '',
        company: user.company || '',
        phone: user.phone || '',
        website: user.website || '',
        about: user.about || '',
      }));

      // Auto-check availability of generated URL
      checkUrlAvailability(generatedUrl);
    }
    
  // Do not depend on templateId: when the user picks a new template, the URL updates and
  // templateId changes; re-running this effect would reload card data and is unnecessary. Previously
  // templateId was in the deps and loadCardData reset templateId from the API, undoing the new pick.
  }, [user, isEditMode, editCardId]);

  const checkUrlAvailability = async (url) => {
    if (!url || url.length < 3) {
      setUrlError('URL must be at least 3 characters long');
      setUrlStatus('invalid');
      return false;
    }
    
    // Check URL format
    if (!/^[a-zA-Z0-9_-]+$/.test(url)) {
      setUrlError('URL can only contain letters, numbers, hyphens, and underscores.');
      setUrlStatus('invalid');
      return false;
    }
    
    setCheckingUrl(true);
    setUrlStatus('checking');
    try {
      let isTaken = false;
      try {
        await api.get(`/cards/url/${url}`);
        isTaken = true;
      } catch (e) {
        if (e.response?.status !== 404) throw e;
      }
      
      // Check against reserved URLs
      const reservedUrls = ['admin', 'test', 'demo', 'api', 'www', 'app', 'dashboard', 'login', 'register', 'analytics', 'templates', 'create', 'settings', 'anvesh'];
      const isReserved = reservedUrls.includes(url.toLowerCase());
      
      if (isTaken) {
        setUrlError('This URL is already taken. Please choose a different one.');
        setUrlStatus('invalid');
        return false;
      } else if (isReserved) {
        setUrlError('This URL is reserved. Please choose a different one.');
        setUrlStatus('invalid');
        return false;
      } else {
        setUrlError('');
        setUrlStatus('valid');
        return true;
      }
    } catch (error) {
      setUrlError('Error checking URL availability');
      setUrlStatus('invalid');
      return false;
    } finally {
      setCheckingUrl(false);
    }
  };

  const handleUrlChange = async (e) => {
    // Handle both event objects and direct string values
    const newUrl = typeof e === 'string' ? e : e.target.value;
    setCardData(prev => ({ ...prev, customUrl: newUrl }));
    
    // Handle empty URL
    if (newUrl.length === 0) {
      setUrlStatus('invalid');
      setUrlError('URL is required');
      return;
    }
    
    // Debounce the URL check
    if (newUrl.length >= 3) {
      setTimeout(() => {
        checkUrlAvailability(newUrl);
      }, 500);
    } else {
      setUrlError('URL must be at least 3 characters long');
      setUrlStatus('invalid');
    }
  };

  const handleSectionToggle = (sectionId) => {
    setSections(prev => prev.map(section => 
      section.id === sectionId 
        ? { ...section, enabled: !section.enabled }
        : section
    ));
  };

  const handleSectionReorderById = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    if (fromId === 'basic-info') return;
    setSections((prev) => {
      const sorted = sortCardSections(prev);
      const basic = sorted.find((s) => s.id === 'basic-info');
      const rest = sorted.filter((s) => s.id !== 'basic-info');
      const fromIndex = rest.findIndex((s) => s.id === fromId);
      if (fromIndex === -1) return prev;
      const toIndex =
        toId === 'basic-info'
          ? 0
          : rest.findIndex((s) => s.id === toId);
      if (toIndex === -1) return prev;
      const next = [...rest];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      const merged = basic ? [basic, ...next] : next;
      return merged.map((s, i) => ({ ...s, order: i + 1 }));
    });
  };

  const handleAddSection = (type = 'custom') => {
    if (type === 'custom') {
      setCustomSections((prev) => [...prev, { enabled: true, title: '', description: '' }]);
      setSections((prev) => prev.map((s) => (s.id === 'custom-sections' ? { ...s, enabled: true } : s)));
      setToast({ open: true, message: 'Added a custom block. Open Custom Sections to edit it.', severity: 'info' });
      return;
    }
    if (type === 'links') {
      setCustomLinks((prev) => {
        const nid = prev.length ? Math.max(...prev.map((x) => Number(x.id) || 0), 0) + 1 : 1;
        return [...prev, { id: nid, title: 'New Link', url: '', enabled: true }];
      });
      setSections((prev) => prev.map((s) => (s.id === 'custom-links' ? { ...s, enabled: true } : s)));
      setToast({ open: true, message: 'Added a link under Custom Links.', severity: 'info' });
      return;
    }
    if (type === 'images') {
      setSections((prev) => prev.map((s) => (s.id === 'gallery' ? { ...s, enabled: true } : s)));
      setToast({ open: true, message: 'Open Image Gallery to upload images.', severity: 'info' });
    }
  };

  const handleRemoveSection = (sectionId) => {
    if (LOCKED_SECTION_IDS.has(sectionId)) return;
    setSections((prev) =>
      prev
        .filter((section) => section.id !== sectionId)
        .map((s, i) => ({ ...s, order: i + 1 }))
    );
  };

  const handleAddSocialLink = () => {
    setSocialLinks((prev) => {
      const nid = prev.length ? Math.max(...prev.map((x) => Number(x.id) || 0), 0) + 1 : 1;
      return [...prev, { id: nid, platform: 'Custom', url: '', enabled: true }];
    });
  };

  const handleRemoveSocialLink = (linkId) => {
    setSocialLinks(prev => prev.filter(link => link.id !== linkId));
  };

  const handleAddCustomLink = () => {
    setCustomLinks((prev) => {
      const nid = prev.length ? Math.max(...prev.map((x) => Number(x.id) || 0), 0) + 1 : 1;
      return [...prev, { id: nid, title: 'New Link', url: '', enabled: true }];
    });
  };

  const handleRemoveCustomLink = (linkId) => {
    setCustomLinks(prev => prev.filter(link => link.id !== linkId));
  };

  const [saving, setSaving] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const doSave = async () => {
    if (!cardData.name || !cardData.email) {
      setToast({ open: true, message: 'Please fill in at least your name and email.', severity: 'warning' });
      return false;
    }
    if (!cardData.customUrl) {
      setToast({ open: true, message: 'Please set a custom URL for your card.', severity: 'warning' });
      return false;
    }
    if (urlStatus === 'invalid' || urlStatus === 'checking') {
      setToast({ open: true, message: 'Please wait for URL check or choose a different URL.', severity: 'warning' });
      return false;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      setToast({ open: true, message: 'Please login to save your card.', severity: 'error' });
      return false;
    }

    setSaving(true);
    try {
      const cardToSave = serializeStructuredCard({
        cardData: {
          ...cardData,
          updatedAt: new Date().toISOString(),
          createdAt: isEditMode ? cardData.createdAt : new Date().toISOString(),
        },
        sections,
        socialLinks,
        customLinks,
        customSections,
        gallerySection,
      });
      if (isEditMode) {
        await api.put(`/cards/url/${editCardId}`, { templateId, cardData: cardToSave });
      } else {
        await api.post('/cards/create', { templateId, cardData: cardToSave });
      }
      setToast({ open: true, message: isEditMode ? 'Card updated!' : 'Card saved!', severity: 'success' });
      return true;
    } catch (error) {
      console.error('Error saving card:', error);
      const apiMsg = error.response?.data?.message;
      const toastMsg =
        typeof apiMsg === 'string' && apiMsg.trim()
          ? apiMsg.trim()
          : 'Failed to save. Please try again.';
      setToast({ open: true, message: toastMsg, severity: 'error' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    await doSave();
  };

  const handlePreview = async () => {
    const ok = await doSave();
    if (ok && cardData.customUrl) {
      window.open(`https://app.webcard.digital/${cardData.customUrl}`, '_blank');
    }
  };

  // Dynamic progress based on filled fields
  const completionPercent = (() => {
    const checks = [
      !!cardData.name,
      !!cardData.title,
      !!cardData.company,
      !!cardData.email,
      !!cardData.phone,
      !!cardData.profileImageUrl,
      !!cardData.about,
      !!cardData.customUrl && urlStatus !== 'invalid',
      !!cardData.website || !!cardData.address,
      socialLinks.some(l => l.enabled && l.url),
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  })();

  // Use module-scope components (defined above) to prevent focus loss from identity changes
  const BasicInfoFields = BasicInfoFieldsOuter;
  const AboutSectionField = AboutSectionFieldOuter;


  return (
    <Box
      sx={{
        display: 'flex',
        // Layout main already reserves 70px under the fixed AppBar; avoid stacking 100vh + toolbar (blank / clipped editor).
        height: { xs: 'auto', md: 'calc(100dvh - 70px)' },
        minHeight: { xs: 'calc(100dvh - 70px)', md: 0 },
        maxHeight: { md: 'calc(100dvh - 70px)' },
        overflow: 'hidden',
      }}
    >
      {/* Left Panel - Customization */}
      <Box sx={{ width: '50%', p: 4, overflow: 'auto', bgcolor: 'background.default' }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
            {isEditMode ? 'Edit Your Card' : 'Create Your Card'}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Customize your digital business card with professional templates
          </Typography>
        </Box>

        {editLoadError && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 1 }} onClose={() => setEditLoadError('')}>
            {editLoadError}
          </Alert>
        )}

        {/* Progress Indicator */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ flex: 1, height: 4, bgcolor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300', borderRadius: 1, mr: 2 }}>
              <Box sx={{ height: '100%', background: completionPercent === 100 ? '#22c55e' : '#667eea', borderRadius: 1, width: `${completionPercent}%`, transition: 'width 0.3s ease' }} />
            </Box>
            <Typography variant="body2" sx={{ color: completionPercent === 100 ? 'success.main' : 'text.secondary', fontWeight: 500, whiteSpace: 'nowrap' }}>
              {completionPercent}% Complete
            </Typography>
          </Box>
        </Box>

        {/* Template Selection */}
        <Card sx={{ mb: 4, borderRadius: 1, boxShadow: editorCardShadow }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Template
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(isEditMode ? `/templates?edit=${editCardId}` : '/templates')}
                sx={{ borderRadius: 1 }}
              >
                Change
              </Button>
            </Box>
            {template && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  label={template.name}
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {template.description}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Custom URL */}
        <Card sx={{ mb: 4, borderRadius: 1, boxShadow: editorCardShadow }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
              Your Custom URL
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Choose a unique URL for your digital business card
            </Typography>
            <FocusTextField
              fullWidth
              label="Link/URL"
              value={cardData.customUrl}
              onChange={handleUrlChange}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              variant="outlined"
              disabled={isEditMode}
              error={urlStatus === 'invalid'}
              helperText={
                isEditMode 
                  ? 'URL cannot be changed when editing existing cards'
                  : urlError || 
                    (checkingUrl ? 'Checking availability...' : '') ||
                    (urlStatus === 'valid' ? '✓ This URL is available!' : '')
              }
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>app.webcard.digital/</Typography>,
                endAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                    {checkingUrl && (
                      <CircularProgress size={16} />
                    )}
                    {urlStatus === 'valid' && !checkingUrl && (
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                    )}
                    {urlStatus === 'invalid' && !checkingUrl && (
                      <CancelIcon sx={{ color: 'error.main', fontSize: 20 }} />
                    )}
                  </Box>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: urlStatus === 'valid' ? 'success.main' : urlStatus === 'invalid' ? 'error.main' : 'primary.main',
                  },
                  '&:hover fieldset': {
                    borderColor: urlStatus === 'valid' ? 'success.main' : urlStatus === 'invalid' ? 'error.main' : 'primary.main',
                  },
                  '& fieldset': {
                    borderColor: urlStatus === 'valid' ? 'success.main' : urlStatus === 'invalid' ? 'error.main' : theme.palette.divider,
                  },
                },
                '& .MuiFormHelperText-root': {
                  color: urlStatus === 'valid' ? 'success.main' : urlStatus === 'invalid' ? 'error.main' : 'text.secondary',
                },
              }}
            />
            {cardData.customUrl && (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1, mt: 1.5,
                px: 1.5, py: 1, borderRadius: 2,
                bgcolor: 'action.hover',
                border: '1px solid', borderColor: 'divider',
              }}>
                <Typography
                  component="a"
                  href={`https://app.webcard.digital/${cardData.customUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    flex: 1, fontSize: '0.85rem', color: 'primary.main',
                    textDecoration: 'none', fontWeight: 500,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  app.webcard.digital/{cardData.customUrl}
                </Typography>
                <IconButton
                  size="small"
                  title="Copy URL"
                  onClick={() => {
                    navigator.clipboard.writeText(`https://app.webcard.digital/${cardData.customUrl}`);
                  }}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                >
                  <ContentCopyIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  size="small"
                  title="Open in new tab"
                  onClick={() => window.open(`https://app.webcard.digital/${cardData.customUrl}`, '_blank')}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                >
                  <OpenInNewIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Sections */}
        {/* Card Sections */}
        <Card sx={{ mb: 4, borderRadius: 1, boxShadow: editorCardShadow }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
              Card Sections
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Customize which sections appear on your digital business card. Drag the grip handle to change order on your card.
            </Typography>

            {sortCardSections(sections).map((section) => (
              <Box
                key={section.id}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const fromId = e.dataTransfer.getData('text/plain');
                  if (fromId && fromId !== section.id) handleSectionReorderById(fromId, section.id);
                }}
              >
                <SectionItemOuter
                  section={section}
                  expandedSections={expandedSections}
                  setExpandedSections={setExpandedSections}
                  onSectionToggle={handleSectionToggle}
                  onRemoveSection={handleRemoveSection}
                  onDragHandleStart={(e) => {
                    e.dataTransfer.setData('text/plain', section.id);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  cardData={cardData}
                  setCardData={setCardData}
                  socialLinks={socialLinks}
                  setSocialLinks={setSocialLinks}
                  onRemoveSocialLink={handleRemoveSocialLink}
                  onAddSocialLink={handleAddSocialLink}
                  customLinks={customLinks}
                  setCustomLinks={setCustomLinks}
                  onRemoveCustomLink={handleRemoveCustomLink}
                  onAddCustomLink={handleAddCustomLink}
                  customSections={customSections}
                  setCustomSections={setCustomSections}
                  gallerySection={gallerySection}
                  setGallerySection={setGallerySection}
                  galleryUploading={galleryUploading}
                  setGalleryUploading={setGalleryUploading}
                  setToast={setToast}
                />
              </Box>
            ))}

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                Add New Section
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleAddSection('custom')}
                    variant="outlined"
                    sx={{ width: '100%', borderRadius: 1, py: 1.5 }}
                  >
                    Custom Section
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleAddSection('links')}
                    variant="outlined"
                    sx={{ width: '100%', borderRadius: 1, py: 1.5 }}
                  >
                    Custom Links
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    startIcon={<CloudUploadIcon />}
                    onClick={() => handleAddSection('images')}
                    variant="outlined"
                    sx={{ width: '100%', borderRadius: 1, py: 1.5 }}
                  >
                    Image Gallery
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Card sx={{ borderRadius: 1, boxShadow: editorCardShadow }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={saving ? <CircularProgress size={18} /> : <PreviewIcon />}
                onClick={handlePreview}
                disabled={saving || urlStatus === 'invalid' || checkingUrl}
                sx={{ flex: 1, py: 1.5, borderRadius: 1 }}
              >
                {saving ? 'Saving...' : 'Save & Preview'}
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving || urlStatus === 'invalid' || checkingUrl}
                sx={{ 
                  flex: 1, 
                  py: 1.5, 
                  borderRadius: 1,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  }
                }}
              >
                {saving ? 'Saving...' : (isEditMode ? 'Update Card' : 'Save Card')}
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast(t => ({ ...t, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))} sx={{ borderRadius: 2 }}>
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>

      {/* Right Panel - Live Preview */}
      <Box sx={{ width: '50%', p: 4, bgcolor: 'background.default', borderLeft: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
            Live Preview
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            See how your digital business card will look
          </Typography>
        </Box>
        
        {/* Phone Mockup */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {(() => {
            const tplName = template?.name || 'Classic Professional';
            const tplCfg = getConfig(tplName);
            let statusBarBg = '#ffffff';
            if (tplCfg.layout === 'dark') {
              statusBarBg = tplCfg.cardBg || '#1e293b';
            } else if (tplCfg.layout === 'hero') {
              statusBarBg = cardData?.bannerImageUrl ? '#f8f9fa' : (tplCfg.headerBg || '#102E5C');
            } else if (tplCfg.layout === 'split') {
              statusBarBg = tplCfg.sidebarBg || tplCfg.accent || '#0d9488';
            }
            return (
              <Box
                sx={{
                  width: 300,
                  height: 600,
                  border: '8px solid #1e293b',
                  borderRadius: '36px',
                  overflow: 'hidden',
                  position: 'relative',
                  boxShadow: '0 25px 50px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(255,255,255,0.1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 80,
                    height: 22,
                    bgcolor: '#1e293b',
                    borderRadius: '0 0 16px 16px',
                    zIndex: 10,
                  },
                }}
              >
                <Box sx={{ height: '100%', overflow: 'auto', background: statusBarBg, pt: 0, borderRadius: '28px' }}>
                  <LivePreviewCard
                    cardData={cardData}
                    templateName={tplName}
                    sections={sections}
                    socialLinks={socialLinks}
                    customLinks={customLinks}
                    customSections={customSections}
                    gallerySection={gallerySection}
                  />
                </Box>
              </Box>
            );
          })()}
        </Box>
      </Box>
    </Box>
  );
};

export default CardCustomizer;
